import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';

import Skeleton from '@material-ui/lab/Skeleton';
import BubbleChart from './BubbleChart';
import Select from '../../Filter/Select/Select';
import { updateChartState, updateZoomState } from '../../../redux/slices/chartFilterSlice';
import { CHART_TYPE } from './chartConstant';
import ColumnChart from './ColumnChart';
import { useStyles } from './styles';
import { IconButton } from '@material-ui/core';
import MaximizeIcon from '@mui/icons-material/ZoomOutMap';
import MinimizeIcon from '@mui/icons-material/ZoomInMap';
import Typography from '@material-ui/core/Typography';

const Chart = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(true);
	const chartFilters = useSelector(state => state.categoryData.chartFilters);
	const filteredProducts = useSelector(state => state.categoryData.filteredProducts);
	const availableChartList = useSelector(state => state.chartFilters.chartState.availableChartList);
	const selectedChart = useSelector(state => state.chartFilters.chartState.selectedChart);
	const chartIsZoomed = useSelector(state => state.chartFilters.zoomState.isZoomed);
	const classes = useStyles();

	/* To display initial skeleton loading */
	useEffect(() => {
		const timer = setTimeout(() => {
			setIsLoading(false);
		}, 500);

		return () => {
			clearTimeout(timer);
		};
	}, [chartFilters]);

	const handleChartSelect = value => {
		dispatch(updateChartState(value));
	};

	const handleMaximize = () => {
		dispatch(updateZoomState(true));
	};

	const handleMinimize = () => {
		dispatch(updateZoomState(false));
	};

	function getChartByType(chartData) {
		switch (chartData.graphType) {
			case CHART_TYPE.BUBBLE:
				return <BubbleChart productTableData={filteredProducts} chartDefinition={chartData} />;
			case CHART_TYPE.COLUMN:
				return <ColumnChart productTableData={filteredProducts} chartDefinition={chartData} />;
			default:
				return null;
		}
	}

	return (
		<Grid item xs>
			{selectedChart && availableChartList.length === 1 && (
				<Typography variant="subtitle1" align="center" style={{ marginBottom: 8, width: '100%', display: 'block' }}>
					{removeSubAndSub(chartFilters.find(chart => removeSubAndSub(chart.gfHeading) === selectedChart)?.gfHeading)}
				</Typography>
			)}
			<div className={classes.chartHeading}>
				{availableChartList.length > 1 ? (
					<>
						<div className={classes.dropDown}>
							<Select
								minWidth={450}
								label={t('main:components.quickFilters.charts.selectTitle')}
								appliedValue={selectedChart}
								values={availableChartList}
								handleSelectChange={handleChartSelect}
								disabled={false}
								tabIndex={0}></Select>
						</div>
						<div className={classes.maximizeButton}>
							{!chartIsZoomed ? (
								<IconButton onClick={() => handleMaximize()} tabIndex={0}>
									<MaximizeIcon />
								</IconButton>
							) : (
								<IconButton onClick={() => handleMinimize()} tabIndex={0}>
									<MinimizeIcon />
								</IconButton>
							)}
						</div>
					</>
				) : null}
			</div>

			{selectedChart ? (
				<Grid>
					{isLoading ? (
						<Skeleton variant="rect" width="100%" height={300} />
					) : (
						getChartByType(chartFilters.find(chart => removeSubAndSub(chart.gfHeading) === selectedChart))
					)}
				</Grid>
			) : null}
		</Grid>
	);
};

export function autoEscapeCharactersForCharts(s) {
	if (!s) {
		return s;
	}
	if (s.includes('[')) {
		s = s.replaceAll('[', '[[');
	}
	if (s.includes(']')) {
		s = s.replaceAll(']', ']]');
	}
	if (s.includes('<sub>')) {
		s = s.replaceAll('<sub>', '[baseline-shift: sub]');
	}
	if (s.includes('</sub>')) {
		s = s.replaceAll('</sub>', '[/]');
	}
	if (s.includes('<sup>')) {
		s = s.replaceAll('<sup>', '[baseline-shift: super]');
	}
	if (s.includes('</sup>')) {
		s = s.replaceAll('</sup>', '[/]');
	}

	return s;
}

export function removeSubAndSub(s) {
	if (!s) {
		return s;
	}
	if (s.includes('<sub>')) {
		s = s.replaceAll('<sub>', '');
	}
	if (s.includes('</sub>')) {
		s = s.replaceAll('</sub>', '');
	}
	if (s.includes('<sup>')) {
		s = s.replaceAll('<sup>', '');
	}
	if (s.includes('</sup>')) {
		s = s.replaceAll('</sup>', '');
	}

	return s;
}

export default Chart;
